import { graphql } from '@/gql';
import type { FetchLovedOneQueryVariables } from '@/gql/graphql';
import type { LovedOne } from '@/models/lovedOne';
import { queryExecute } from '@/Utils/graphqlAPI';

export const fetchLovedOne = async ({ id, email, phoneNumber, isIncludeCallers }: FetchLovedOneQueryVariables): Promise<LovedOne> => {
  const response = await queryExecute({
    query: graphql(`
      query FetchLovedOne($id: String, $phoneNumber: String, $email: String, $isIncludeCallers: Boolean) {
        lovedOne(id: $id, phoneNumber: $phoneNumber, email: $email, isIncludeCallers: $isIncludeCallers) {
          id
          prefix
          firstName
          lastName
          relationship
          phoneNumber
          transactionalSms
          email
          associatedCallers
          timeZone
          noCalls
          transactionalSms
          noMail
          marketingMail
          chargeFailedSms
          disputeFailedSms
          rideCreditRenewalSms
          driverAcceptedRideSms
          driverCancelledRideSms
          noDriversAvailableSms
          redispatchedRideSms
          rideInProgressSms
          rideCompletedSms
          operatorCancelledRideSms
          riderCancelledRideSms
          scheduledRideConfirmationSms
          scheduledRideSurgeSmsForCaller
          scheduledRideCancelledSms
          scheduledRideUpdatedSms
          scheduledGroceryConfirmationSms
          scheduledGroceryCancelledSms
          scheduledGroceryUpdatedSms
          doNotTextDriverSms
          unsubscribedSms
          resubscribedSms
          listedAsEmergencyContactSms
          welcomeEmail
          deactivationPassedAwayEmail
          driverArriving
          scheduledRideSurgeCall
          scheduledRideCall
          pricePendingRideCall
          callRiderPriceChange
          callRiderDriverCanceled
          cancellationFeeDisputeFailedCall
          sendNeedLoveAutomatedCall
          sendNeedLoveRefundAutomatedCall
          callRiderDriverDetails
          pricePendingRideAfterDateExpiredCall
          callRiderTripCost
          callNewDriverInfo
          callWarnNoShowFee
          scheduledRidePayForAnyUnpaidServicesFailedCall
          scheduledRideUnpaidServicesCallIn24Hours
          scheduledRidePreauthFailedCallIn24Hours
          scheduledRideReminderDayBeforeCall
          scheduledRideCustomReminderCall
          scheduledGroceryPayForAnyUnpaidServicesFailedCall
          scheduledGroceryUnpaidServicesCallIn24Hours
          scheduledGroceryPreauthFailedCallIn24Hours
          scheduledGroceryBeforeTheDayCall
          scheduledGroceryCustomReminderCall
          scheduledDeliveryPayForAnyUnpaidServicesFailedCall
          scheduledDeliveryUnpaidServicesCallIn24Hours
          scheduledDeliveryPreauthFailedCallIn24Hours
          scheduledDeliveryReminderCall
          callPartnerCallerAboutPartnerCredits
          callPartnerCallerAboutPartnerRides
          callWarnInProgressDriverCancellation
          subscriptionChargeSuccessCall
          subscriptionChargeFailedCall
          weeklyCheckInMarketingCall
          groceryOrderShoppingCall
          groceryOrderShoppingCompleteCall
          groceryOrderInTransitCall
          groceryOrderCanceledCall
          groceryOrderReminderCall
          membershipRenewalReminderCall
          membershipSuspensionWillEndCall
          membershipTrialWillEndIn3DaysCall
          marketingCalls
          ridesMarketingCalls
          gourmetMarketingCalls
          groceriesMarketingCalls
          deliveryArrivalCall
          deliveryAfterOrderCall
          deliveryAfterHoursOrderStatusUpdateCall
          groceryOrderOutsideReminderCall
          refundDepositDeniedCall
          priceConfirm
          isSendUserPhoneNumberInDriverAcceptedTextMessage
          crmActivationCampaignSms
          crmActivationDormantHardRegistrationCampaignSms
          crmActivationDormantSoftRegistrationCampaignSms
          crmActivationHardRegistrationCampaignSms
          crmActivationNewUserAddedCCRegistrationSms
          crmActivationNewUserGourmetRegistrationSms
          crmActivationNewUserGroceryRegistrationSms
          crmActivationNewUserOnlineRegistrationSms
          crmActivationNewUserPhoneRegistrationSms
          crmActivationNewUserPickedPlanRegistrationSms
          crmCrossSellGourmetCampaignSms
          crmCrossSellGroceriesCampaignSms
          crmCrossSellHomeServicesCampaignSms
          crmMomentumEngagementCampaignSms
          crmReactivationCampaignSms
          crmResurrectionCampaignSms
          crmRevivalCampaignSms
          deliveryArrivalSms
          marketingSms
          groceryOrderOutsideReminderSms
          transactionalEmails
          addPaymentSourceEmail
          updatePaymentSourceEmail
          defaultPaymentSourceEmail
          deletePaymentSourceEmail
          apologyRefundEmail
          blockDriverEmail
          cancellationFeeDisputeSuccessEmail
          cancellationFeeDisputeFailedEmail
          closingAccountEmail
          falseTripDisputeFailedEmail
          falseTripDisputeSuccessEmail
          fareDisputeSuccessEmail
          fareDisputeFailedEmail
          findLostItemFailedEmail
          refundEmail
          giftCardNotificationEmail
          giftCardReceiptEmail
          membershipRenewalReminderEmail
          membershipConfirmationEmail
          membershipFailedEmail
          membershipReceiptEmail
          membershipTrialConfirmationEmail
          membershipTrialWillEndEmail
          gogoGourmetReceiptEmail
          needLoveRefundEmail
          newRideCreditsNotificationEmail
          noPaymentMethodOnFileEmail
          noRidesCallerEmail
          noShowReceiptEmail
          partnerCreditsNotificationEmail
          partnerRidesNotificationEmail
          refundRideEmail
          rideCancellationReceiptEmail
          rideCreditsRenewalEmail
          rideHistoryEmail
          rideReceiptEmail
          scheduledRideCancelledEmail
          scheduledRideChangedEmail
          scheduledRideConfirmedEmail
          scheduledRideReminderDayBeforeEmail
          scheduledRideUnpaidServicesEmail
          scheduledRidePreauthFailedEmail
          scheduledGroceryCanceledEmail
          scheduledGroceryChangedEmail
          scheduledGroceryConfirmedEmail
          scheduledGroceryReminderEmail
          scheduledGroceryUnpaidServicesEmail
          scheduledGroceryPreauthFailedEmail
          scheduledDeliveryReminderEmail
          scheduledDeliveryUnpaidServicesEmail
          scheduledDeliveryPreauthFailedEmail
          surgePricingWasConfirmedEmail
          welcomeEmailForUsersWithPartners
          suspendingAccountMembershipEmail
          groceryReceiptEmail
          groceryReceivedEmail
          groceryOrderReminderEmail
          refundDepositDeniedEmail
          accountReactivateEmail
          driverCanceledEmail
          freeTrialReminderEmail
          groceryOrderUpdateEmail
          homeServicesReceiptEmail
          marketingEmails
          crmActivationCampaignEmail
          crmActivationHardRegistrationCampaignEmail
          crmMomentumEngagementCampaignEmail
          crmReactivationCampaignEmail
          crmRevivalCampaignEmail
          crmResurrectionCampaignEmail
          crmCrossSellGourmetCampaignEmail
          crmCrossSellGroceriesCampaignEmail
          crmCrossSellHomeServicesCampaignEmail
          crmActivationDormantSoftRegistrationCampaignEmail
          crmActivationDormantHardRegistrationCampaignEmail
          crmActivationNewUserAddedCCRegistrationEmail
          crmActivationNewUserGroceryRegistrationEmail
          crmActivationNewUserGourmetRegistrationEmail
          crmActivationNewUserOnlineRegistrationEmail
          crmActivationNewUserPhoneRegistrationEmail
          crmActivationNewUserPickedPlanRegistrationEmail
          monthlyInvoiceMail
          gourmetRestaurantsMail
          reactivationPostcardsMail
          happyBirthdayPostcardsMail
        }
      }
    `),
    variables: {
      id,
      email,
      phoneNumber,
      isIncludeCallers,
    },
    fetchPolicy: 'no-cache',
  });

  return response.data?.lovedOne!;
};
